import * as React from "react"
import {Chip, LinearProgress, Box, Typography, IconButton, Tooltip} from "@mui/material"
import {IconSeeker} from "@ui-seeker/core"

import UserCell from "./UserCell"
import {batchesTableData} from "../@signals"


const statusColors = {
	initializing: 'warning',
	creating: 'info',
	processing: 'warning',
	ready: 'success',
	failed: 'error',
	cancelled: 'default'
}

// Helper function to format relative time
const getRelativeTime = (dateString) => {
	if (!dateString) return '-'
	
	const date = new Date(dateString)
	const now = new Date()
	const diffMs = now - date
	const diffSecs = Math.floor(diffMs / 1000)
	const diffMins = Math.floor(diffSecs / 60)
	const diffHours = Math.floor(diffMins / 60)
	const diffDays = Math.floor(diffHours / 24)
	const diffWeeks = Math.floor(diffDays / 7)
	const diffMonths = Math.floor(diffDays / 30)
	const diffYears = Math.floor(diffDays / 365)
	
	if (diffSecs < 60) return 'hace unos segundos'
	if (diffMins < 60) return `hace ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`
	if (diffHours < 24) return `hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`
	if (diffDays < 7) return `hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`
	if (diffWeeks < 4) return `hace ${diffWeeks} ${diffWeeks === 1 ? 'semana' : 'semanas'}`
	if (diffMonths < 12) return `hace ${diffMonths} ${diffMonths === 1 ? 'mes' : 'meses'}`
	return `hace ${diffYears} ${diffYears === 1 ? 'año' : 'años'}`
}

export const batchIDColumn = (selectedBatchID = null) => ({
    name: 'batchID',
    label: 'Batch ID',
    options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                    width: 4,
                    height: 20,
                    mr: 1,
                    borderRadius: 1,
                    bgcolor: selectedBatchID === value ? 'info.main' : 'transparent'
                }} />
                <Typography variant="body2" fontWeight="bold">#{value}</Typography>
            </Box>
        )
    }
})

export const nameColumn = () => ({
	name: 'name',
	label: 'Name',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value, tableMeta) => {
			const rowData = tableMeta.rowData
			const autovalidated = rowData[14] // autovalidated is at index 14
			// Every other field here is read by column index; idSchedule is not one of
			// them, and inserting a column would shift the ~20 hardcoded indices in this
			// file. Looking the row up by batchID keeps the ordering untouched.
			const batch = batchesTableData.value.find(b => b.batchID === rowData[0])

			return (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
					<Typography variant="body2">{value || '-'}</Typography>
					{batch?.idSchedule && (
						<Tooltip title={batch.scheduleName ? `Created by schedule "${batch.scheduleName}"` : 'Created by a schedule'}>
							<Chip
								label="Recurrent"
								size="small"
								color="info"
								icon={<IconSeeker icon="repeat" />}
								sx={{
									height: 20,
									fontSize: '0.7rem',
									'& .MuiChip-icon': { fontSize: '0.8rem' }
								}}
							/>
						</Tooltip>
					)}
					{autovalidated && (
						<Chip 
							label="Auto" 
							size="small" 
							color="success"
							icon={<IconSeeker icon="wand-magic-sparkles" />}
							sx={{ 
								height: 20, 
								fontSize: '0.7rem',
								'& .MuiChip-icon': { fontSize: '0.8rem' }
							}}
						/>
					)}
				</Box>
			)
		}
	}
})

export const statusColumn = () => ({
	name: 'status',
	label: 'Status',
	options: {
		filter: true,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value, tableMeta) => {
			const rowData = tableMeta.rowData
			const pendingQa = rowData[7] || 0
			const pendingTasks = rowData[8] || 0
			
			// If QA tasks are 100% complete (no pending tasks), mark as finished
			let displayStatus = value
			if (pendingQa > 0 && pendingTasks === 0) {
				displayStatus = 'finished'
			}

			// Why it failed, when the normalizer closed the batch because its selection
			// resolved to nothing (no products in the window, no sites with data for the
			// brand…). Stored in the batch config by the normalizer (869fmzrmg); read off
			// the row by batchID like the schedule chip, so no column index moves.
			const batch = batchesTableData.value.find(b => b.batchID === rowData[0])
			const failureReason = displayStatus === 'failed' ? batch?.config?.failure_reason : null

			const chip = (
				<Chip 
					label={displayStatus?.toUpperCase() || 'UNKNOWN'} 
					color={statusColors[displayStatus] || 'default'}
					size="small"
				/>
			)
			return failureReason
				? <Tooltip title={failureReason}><span>{chip}</span></Tooltip>
				: chip
		}
	}
})

export const totalTasksVisibleColumn = () => ({
	name: 'totalTasks',
	label: 'Total Tasks',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value) => (
			<Typography variant="body2" fontWeight="medium">
				{value || 0}
			</Typography>
		)
	}
})

export const totalTasksColumn = () => ({
	name: 'totalTasks',
	label: 'Total Tasks',
	options: {
		filter: false,
		sort: true,
		display: false
	}
})

export const completedTasksColumn = () => ({
	name: 'completedTasks',
	label: 'Completed',
	options: {
		filter: false,
		sort: true,
		display: false
	}
})

export const failedTasksColumn = () => ({
	name: 'failedTasks',
	label: 'Failed',
	options: {
		filter: false,
		sort: true,
		// Hidden by default (still toggleable from the columns menu): the Progress
		// column already surfaces the failed count where it means something.
		display: false,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value) => (
			<Typography variant="body2" color={value > 0 ? 'error.main' : 'text.secondary'}>
				{value || 0}
			</Typography>
		)
	}
})

export const qaTasksColumn = () => ({
	name: 'pendingQa',
	label: 'QA Tasks',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value, tableMeta) => {
			const rowData = tableMeta.rowData
			const totalQaTasks = value || 0
			const pendingTasks = rowData[8] || 0 // pendingTasks column index (updated)
			const completedQaTasks = Math.max(0, totalQaTasks - pendingTasks)
			const progress = totalQaTasks > 0 ? (completedQaTasks / totalQaTasks) * 100 : 0
			
			if (totalQaTasks === 0) {
				return (
					<Typography variant="body2" color="text.secondary">
						-
					</Typography>
				)
			}
			
			return (
				<Box sx={{ width: '100%', minWidth: 150 }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
						<Typography variant="caption" color="text.secondary">
							{completedQaTasks}/{totalQaTasks} tasks
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{progress.toFixed(1)}%
						</Typography>
					</Box>
					<LinearProgress 
						variant="determinate" 
						value={progress} 
						sx={{ 
							height: 6, 
							borderRadius: 1,
							backgroundColor: 'action.hover',
							'& .MuiLinearProgress-bar': {
								backgroundColor: getPercentageColor(progress)
							}
						}}
					/>
				</Box>
			)
		}
	}
})

export const autoTasksColumn = () => ({
	name: 'autoTasks',
	label: 'Auto Mapping',
	options: {
		filter: false,
		sort: false,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value, tableMeta) => {
			const rowData = tableMeta.rowData
			const automappedEan = rowData[4] || 0
			const automappedIa = rowData[5] || 0
			const autoexcluded = rowData[6] || 0
			
			return (
				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
					<Chip label={`EAN: ${automappedEan}`} size="small" color="success" variant="outlined" />
					<Chip label={`IA: ${automappedIa}`} size="small" color="info" variant="outlined" />
					<Chip label={`Excluded: ${autoexcluded}`} size="small" color="default" variant="outlined" />
				</Box>
			)
		}
	}
})

export const automappedEanColumn = () => ({
	name: 'automappedEan',
	label: 'Automapped EAN',
	options: {
		filter: false,
		sort: true,
		display: false
	}
})

export const automappedIaColumn = () => ({
	name: 'automappedIa',
	label: 'Automapped IA',
	options: {
		filter: false,
		sort: true,
		display: false
	}
})

export const autoexcludedColumn = () => ({
	name: 'autoexcluded',
	label: 'Autoexcluded',
	options: {
		filter: false,
		sort: true,
		display: false
	}
})

export const pendingTasksColumn = () => ({
	name: 'pendingTasks',
	label: 'Pending Tasks',
	options: {
		filter: false,
		sort: true,
		display: false
	}
})

export const createdAtColumn = () => ({
	name: 'createdAt',
	label: 'Created',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value) => {
			if (!value) return '-'
			const date = new Date(value)
			const exactDate = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
			
			return (
				<Tooltip title={exactDate} arrow>
					<Typography variant="body2" sx={{ whiteSpace: 'nowrap', cursor: 'help' }}>
						{getRelativeTime(value)}
					</Typography>
				</Tooltip>
			)
		}
	}
})

export const userColumn = (users) => ({
	name: 'userName',
	label: 'User',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value, tableMeta) => {
			const rowData = tableMeta.rowData
			const batchID = rowData[0]
			const status = rowData[2] // Status is at index 2
			const pendingQa = rowData[7] || 0
			const pendingTasks = rowData[8] || 0
			const userID = rowData[13] // userID is at index 13
			
			// Calculate display status (same logic as statusColumn)
			let displayStatus = status
			if (pendingQa > 0 && pendingTasks === 0) {
				displayStatus = 'finished'
			}
			
			return (
				<UserCell 
					batchID={batchID}
					userName={value}
					userID={userID}
					status={displayStatus}
					users={users}
				/>
			)
		}
	}
})

// Only raw2raw, war2war and sku2raw carry a customer; raw2sku shows a dash rather than
// an empty cell, so "this batch has no customer" reads as a fact and not as missing data.
export const customerColumn = () => ({
	name: 'customerName',
	label: 'Customer',
	options: {
		filter: false,   // the real filter is the toolbar select: it is applied server-side
		sort: true,
		customBodyRender: (value) => value
			? <Box sx={{ fontSize: '0.875rem' }}>{value}</Box>
			: <Box sx={{ color: 'text.disabled' }}>—</Box>
	}
})

export const userIDColumn = () => ({
	name: 'userID',
	label: 'User ID',
	options: {
		filter: false,
		sort: false,
		display: false
	}
})

export const autovalidatedColumn = () => ({
	name: 'autovalidated',
	label: 'Autovalidated',
	options: {
		filter: false,
		sort: false,
		display: false
	}
})


// Helper function to get color based on percentage
const getPercentageColor = (pct) => {
	if (pct === null || pct === undefined) return 'text.secondary'
	if (pct >= 80) return 'success.main'
	if (pct >= 60) return 'primary.main'
	if (pct >= 40) return 'warning.main'
	return 'error.main'
}

// Helper function to render percentage with visual appeal
const renderPercentage = (value) => {
	if (value === null || value === undefined) {
		return (
			<Typography variant="body2" color="text.secondary" fontStyle="italic">
				N/A
			</Typography>
		)
	}
	
	const color = getPercentageColor(value)
	const pct = parseFloat(value).toFixed(1)
	
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
			<Typography 
				variant="body2" 
				fontWeight="bold" 
				sx={{ color, fontSize: '1rem' }}
			>
				{pct}%
			</Typography>
			<LinearProgress 
				variant="determinate" 
				value={Math.min(parseFloat(value), 100)}
				sx={{ 
					width: '60px', 
					height: 6, 
					borderRadius: 1,
					backgroundColor: 'action.hover',
					'& .MuiLinearProgress-bar': {
						backgroundColor: color
					}
				}}
			/>
		</Box>
	)
}

// %% of ALL batch tasks resolved automatically (1st pass EAN/IA or the AI second
// pass, mapped or excluded). Pending QA tasks count against it by design.
export const autoMappingPctColumn = () => ({
	name: 'autoMappingPct',
	label: 'Auto %',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value) => (
			<Tooltip title="Share of ALL batch tasks resolved automatically (1st pass or AI 2nd pass, mapped or excluded)">
				<Box>{renderPercentage(value)}</Box>
			</Tooltip>
		)
	}
})

export const credibilityPctColumn = () => ({
	name: 'credibilityPct',
	label: 'Credibility %',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value) => renderPercentage(value)
	}
})

// Results split over FINISHED tasks: real mappings (found) vs excluded/not_found.
// found + not_found = 100 by construction, so the bar needs only foundPct.
export const resultsColumn = () => ({
	name: 'foundPct',
	label: 'Results',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (value) => {
			if (value == null) return <Typography variant="body2" color="text.secondary">-</Typography>
			const nf = Math.max(0, 100 - value)
			return (
				<Tooltip title={`${value.toFixed(1)}% product found · ${nf.toFixed(1)}% not found (manual + auto)`}>
					<Box sx={{ minWidth: 120 }}>
						<Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
							<Box sx={{ width: `${value}%`, bgcolor: 'success.main' }} />
							<Box sx={{ width: `${nf}%`, bgcolor: 'error.main' }} />
						</Box>
						<Typography variant="caption" color="text.secondary">
							{value.toFixed(0)}% found · {nf.toFixed(0)}% NF
						</Typography>
					</Box>
				</Tooltip>
			)
		}
	}
})

// In-flight states of a second pass running on the OpenAI Batch queue (no
// per-task progress available until the batch resolves). Mirrors the
// normalizer's batch sub-states — see SecondPassDialog's QUEUED_STATES.
const SECOND_PASS_QUEUED_STATES = ['building', 'waiting_picks', 'ingesting_picks',
                                   'waiting_verify', 'ingesting_verify']

export const secondPassColumn = (onAiPass) => ({
    name: 'secondPass',
    label: 'AI Pass',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (sp, tableMeta) => {
            if (!sp || (!sp.active && !sp.runs)) return <Typography variant="body2" color="text.disabled">-</Typography>
            const batchID = tableMeta.rowData[0]
            const batchName = tableMeta.rowData[1]
            const open = () => onAiPass && onAiPass(batchID, batchName)

            if (sp.active) {
                const a = sp.active
                const applying = a.status === 'applying'
                // Batch-mode runs sit in the OpenAI queue (build/wait/ingest) with no
                // per-task progress — a percentage there would read as stuck at 0%.
                const queued = SECOND_PASS_QUEUED_STATES.includes(a.status)
                const pct = applying
                    ? (a.applyTarget > 0 ? Math.round((a.applied / a.applyTarget) * 100) : 0)
                    : (a.totalTasks > 0 ? Math.round((a.evaluated / a.totalTasks) * 100) : 0)
                return (
                    <Tooltip title={queued
                        ? `AI queued at OpenAI (${a.status}) — results within 24h · click to open`
                        : applying
                            ? `Applying decisions: ${a.applied}/${a.applyTarget} — click to open`
                            : `AI evaluating: ${a.evaluated}/${a.totalTasks} — click to open`}>
                        <Chip
                            size="small"
                            color={applying ? 'success' : 'info'}
                            variant={queued ? 'outlined' : 'filled'}
                            icon={<IconSeeker icon="robot" sx={{ fontSize: '0.8rem' }} />}
                            label={queued ? 'AI queued' : `${applying ? 'applying' : 'AI'} ${pct}%`}
                            onClick={open}
                            sx={{ cursor: onAiPass ? 'pointer' : 'default' }}
                        />
                    </Tooltip>
                )
            }
            // Finished runs: aggregated results of every executed second pass
            return (
                <Tooltip title={
                    `${sp.runs} AI pass${sp.runs > 1 ? 'es' : ''} — validated ${sp.validated} · ` +
                    `not found ${sp.notFound} · control sample ${sp.sampled} · left for QA ${sp.forQa}` +
                    (sp.errors > 0 ? ` · errors ${sp.errors}` : '') + ' — click to open'
                }>
                    <Chip
                        size="small"
                        variant="outlined"
                        color="secondary"
                        icon={<IconSeeker icon="robot" sx={{ fontSize: '0.8rem' }} />}
                        label={`${sp.validated}✓ ${sp.notFound}∅ ${sp.forQa}QA`}
                        onClick={open}
                        sx={{ cursor: onAiPass ? 'pointer' : 'default' }}
                    />
                </Tooltip>
            )
        }
    }
})


export const progressColumn = () => ({
	name: 'progress',
	label: 'Progress',
	options: {
		filter: false,
		sort: true,
		setCellHeaderProps: () => ({ align: 'center' }),
		setCellProps: () => ({ align: 'center' }),
		customBodyRender: (_value, tableMeta) => {
			// Reads the counter columns appended after this one (see Table/index.jsx).
			const total = tableMeta.rowData[16] || 0
			const completed = tableMeta.rowData[17] || 0
			const failed = tableMeta.rowData[18] || 0
			if (!total) return <Typography variant="body2" color="text.secondary">-</Typography>
			const done = completed + failed
			// A batch that stopped short of its total is stuck, not progressing:
			// showing it plainly is what saves a trip to the logs.
			return (
				<Typography variant="body2" color={failed > 0 ? 'error.main' : 'text.secondary'}>
					{done}/{total}{failed > 0 ? ` (${failed} failed)` : ''}
				</Typography>
			)
		}
	}
})


export const actionsColumn = (onViewDetails, onViewAllItems, onAutovalidate, onForceFinalize, onResetExcluded, onDeleteBatch, deletingBatchID, finalizingBatchID, resettingBatchID, selectedBatchID, onAiPass, onRetryTasks, retryingBatchID) => ({
    name: 'actions',
    label: 'Actions',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value, tableMeta) => {
            const rowData = tableMeta.rowData
            const batchID = rowData[0]
            const batchName = rowData[1]
            const status = rowData[2]
            const autoexcluded = rowData[6] || 0
            const totalQaTasks = rowData[7] || 0
            const pendingTasks = rowData[8] || 0
            const completedQaTasks = Math.max(0, totalQaTasks - pendingTasks)
            const isDeleting = deletingBatchID === batchID
            const isFinalizing = finalizingBatchID === batchID
            const isResetting = resettingBatchID === batchID
            const isRetrying = retryingBatchID === batchID
            // Indices 16-18: the counter columns appended at the END of the column
            // array (see Table/index.jsx). Anything inserted before index 8 shifts
            // the reads above, which is why they go last.
            const totalTasks = rowData[16] || 0
            const completedTasks = rowData[17] || 0
            const failedTasks = rowData[18] || 0

            // Show autovalidate only if batch is ready and has pending tasks
            const canAutovalidate = status === 'ready' && pendingTasks > 0

            // AI second pass: only wired for raw2raw (handler passed on that tab)
            const canAiPass = !!onAiPass && (status === 'ready' || status === 'finished') && pendingTasks > 0

            // Show force finalize only if batch is in creating status
            const canForceFinalize = status === 'creating'

            // Retry: a batch still running that cannot reach its total on its own.
            // Cloud Tasks drops a task after a few sub-second attempts, so any real
            // outage leaves completed + failed < total forever ('creating' limbo).
            // 'processing' is included too — it hangs exactly the same way.
            // A 'failed' batch is retriable too: the normalizer closes it that way when
            // its selection found nothing, and once the data is fixed (brand assigned,
            // window widened) the same batch re-initialises with its stored config.
            const isRunning = status === 'creating' || status === 'processing'
            const canRetry = status === 'failed'
                || (isRunning && (totalTasks === 0 || completedTasks + failedTasks < totalTasks))

            // Show reset excluded when batch is ready and has auto-excluded items
            const canResetExcluded = status === 'ready' && autoexcluded > 0

            // Show View All Items when batch is creating, ready or finished
            const canViewAllItems = status === 'creating' || status === 'ready' || status === 'finished' || (totalQaTasks > 0)

            return (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Box className={selectedBatchID === batchID ? 'is-open' : undefined} sx={{ display: 'none' }} />
                    <Tooltip title="View Details">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onViewDetails && onViewDetails(batchID)}
                            disabled={isDeleting}
                        >
                            <IconSeeker icon="eye" />
                        </IconButton>
                    </Tooltip>
                    {canViewAllItems && (
                        <Tooltip title="View All Items">
                            <IconButton
                                size="small"
                                color="info"
                                onClick={() => onViewAllItems && onViewAllItems(batchID, batchName)}
                                disabled={isDeleting}
                            >
                                <IconSeeker icon="list-check" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canAutovalidate && (
                        <Tooltip title="Autovalidate">
                            <IconButton
                                size="small"
                                color="success"
                                onClick={() => onAutovalidate && onAutovalidate(batchID)}
                                disabled={isDeleting || isFinalizing}
                            >
                                <IconSeeker icon="wand-magic-sparkles" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canAiPass && (
                        <Tooltip title="AI Second Pass">
                            <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => onAiPass(batchID, batchName)}
                                disabled={isDeleting || isFinalizing}
                            >
                                <IconSeeker icon="robot" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canRetry && (
                        <Tooltip title={status === 'failed' ? 'Re-initialise the failed batch with its stored config' : `Retry pending tasks (${completedTasks + failedTasks}/${totalTasks} done)`}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onRetryTasks && onRetryTasks(batchID, batchName, totalTasks - completedTasks - failedTasks)}
                                disabled={isDeleting || isRetrying || isFinalizing}
                            >
                                <IconSeeker icon={isRetrying ? "spinner" : "rotate"} isSpin={isRetrying} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canForceFinalize && (
                        <Tooltip title="Force Finalize">
                            <IconButton
                                size="small"
                                color="warning"
                                onClick={() => onForceFinalize && onForceFinalize(batchID, batchName)}
                                disabled={isDeleting || isFinalizing}
                            >
                                <IconSeeker icon={isFinalizing ? "spinner" : "circle-check"} isSpin={isFinalizing} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canResetExcluded && (
                        <Tooltip title="Reset excluded to pending">
                            <IconButton
                                size="small"
                                color="warning"
                                onClick={() => onResetExcluded && onResetExcluded(batchID, batchName, autoexcluded)}
                                disabled={isDeleting || isResetting}
                            >
                                <IconSeeker icon={isResetting ? "spinner" : "clock-rotate-left"} isSpin={isResetting} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Delete Batch">
                        <IconButton
                            size="small"
                            color="default"
                            onClick={() => onDeleteBatch && onDeleteBatch(batchID, batchName)}
                            disabled={isDeleting}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'error.main'
                                }
                            }}
                        >
                            <IconSeeker icon={isDeleting ? "spinner" : "trash"} isSpin={isDeleting} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    }
})
